import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { navLinks, CONTACT_PHONE_DISPLAY } from "@/mocks/siteData";
import { waLink, telLink } from "@/lib/links";
import BrandLogo from "@/components/feature/BrandLogo";
import LanguageSwitcher from "@/components/feature/LanguageSwitcher";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || menuOpen;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-20 flex items-center justify-between gap-4">
        <BrandLogo variant={solid ? "dark" : "light"} />

        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={`text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                solid
                  ? "text-foreground-700 hover:text-primary-600"
                  : "text-background-50/90 hover:text-primary-300"
              }`}
            >
              {t(link.key)}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={telLink}
            className={`flex items-center gap-2 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${
              solid ? "text-foreground-800 hover:text-primary-600" : "text-background-50 hover:text-primary-300"
            }`}
          >
            <i className="ri-phone-fill w-4 h-4 flex items-center justify-center" />
            {CONTACT_PHONE_DISPLAY}
          </a>
          <a
            href={waLink(t("wa.nav"))}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition"
          >
            <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center" />
            {t("nav.whatsapp")}
          </a>
          <LanguageSwitcher variant={solid ? "dark" : "light"} />
        </div>

        <div className="flex lg:hidden items-center gap-2">
          <LanguageSwitcher variant={solid ? "dark" : "light"} />
          <button
            type="button"
            aria-label={t("nav.home")}
            onClick={() => setMenuOpen((v) => !v)}
            className={`w-10 h-10 flex items-center justify-center rounded-md cursor-pointer transition ${
              solid ? "text-foreground-800 bg-background-100" : "text-background-50 bg-background-50/15"
            }`}
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl w-6 h-6 flex items-center justify-center`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50">
          <nav className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="px-3 py-3 rounded-md text-sm font-semibold text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition cursor-pointer"
              >
                {t(link.key)}
              </a>
            ))}
            <div className="flex flex-col sm:flex-row gap-3 pt-3 mt-2 border-t border-background-200">
              <a
                href={telLink}
                className="flex-1 inline-flex items-center justify-center gap-2 border border-background-300 text-foreground-800 text-sm font-semibold px-5 py-3 rounded-md whitespace-nowrap cursor-pointer transition"
              >
                <i className="ri-phone-fill w-4 h-4 flex items-center justify-center" />
                {t("nav.callNow")}
              </a>
              <a
                href={waLink(t("wa.nav"))}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold px-5 py-3 rounded-md whitespace-nowrap cursor-pointer transition"
              >
                <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center" />
                {t("nav.whatsapp")}
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}